/**
 * editing redux reducer
 * 
 * @author    Jens Gruschel, Maurizio Tidei
 * @copyright © 2018 contexagon GmbH
 */


import {
    LOAD_OBJECT,
    SAVE_OBJECT,
    SWITCH_OBJECT,
    CREATE_NEW_OBJECT,
    UPDATE_WHOLE_OBJECT,
    UPDATE_OBJECT_VALUE,
    UPDATE_OBJECT_MAP,
    POP_EDIT_STACK,
    ASSIGN_ELEMENT,
    PUSH_ELEMENT,
    REMOVE_FIELD_ELEMENT,
    MOVE_FIELD_ELEMENT,
    DELETE_OBJECT_TAG,
    ADD_OBJECT_TAG,
    LOAD_ALL_TAGS,
    CHANGE_TAB,
    SET_EDIT_FLAG,
    CREATE_NEW_REFERENCE,
    CREATE_NEW_ELEMENT,
    FETCH_REFERENCABLE_OBJECTS,
    FETCH_REFERENCED_OBJECTS
} from './consts';
import {
    selectObjectById,
    selectCurrentObjectId,
    selectCurrentReferenceFieldPath,
    selectCurrentElementFieldPath,
    selectCurrentFlags
} from './selectors';
import {generateTemporaryObjectId} from '../../utils';
import {ACCESS_WRITABLE} from '../authorization/consts';
import {
    updateObjects,
    clearStack,
    pushObject,
    popObject,
    switchObjectId,
    setObject,
    setObjectValue,
    setWholeObject,
    setTabIndex,
    setEditFlag
} from './modifiers';
import _ from 'lodash';


const initialObjectInfo = {
    accessLevel: ACCESS_WRITABLE,
    fieldsetAccessLevels: {},
    referencedBy: []
};

const initialState = {
    objects: {},
    originalObjects: {},
    modified: {},
    editStack: [],
    referencableObjects: [], // list of all objects allowed to be referenced for some field (such as all media items)
    objectInfo: {},
    allTags: [] // list of all tags available in the database
};


export default function editingReducer(state = initialState, action) {

    switch (action.type) {

        case CHANGE_TAB: {
            return setTabIndex(state, action.tabKey, action.index);
        }

        case SET_EDIT_FLAG: {
            return setEditFlag(state, action.flagName, action.flagValue);
        }

        case SAVE_OBJECT: {
            return setObject(state, action.object.id, action.object, true);
        }

        case SWITCH_OBJECT: {
            return switchObjectId(state, action.objectId, action.accessLevel);
        }

        case UPDATE_WHOLE_OBJECT: {
            return setWholeObject(state, action.objectId, action.object);
        }

        case UPDATE_OBJECT_VALUE: {
            return setObjectValue(state, action.objectId, action.fieldPath, action.value);
        }

        case POP_EDIT_STACK: {
            return popObject(state);
        }

        case ASSIGN_ELEMENT: {

            const fieldPath = selectCurrentReferenceFieldPath(state) || selectCurrentElementFieldPath(state);
            const flags = selectCurrentFlags(state);
            const popState = popObject(state);
            const objectId = selectCurrentObjectId(popState);
            const object = selectObjectById(popState, objectId);
            if (Number.isInteger(fieldPath[fieldPath.length - 1])) {
                return setObjectValue(popState, objectId, fieldPath, action.value);
            }
            else if (flags.replace) {
                return setObjectValue(popState, objectId, fieldPath, [action.value]);
            }
            else {
                const items = _.get(object, fieldPath, []);
                const newItems = [...items, action.value];
                return setObjectValue(popState, objectId, fieldPath, newItems);
            }
        }

        case REMOVE_FIELD_ELEMENT: {

            const object = selectObjectById(state, action.objectId);
            const elements = _.get(object, action.fieldPath, []);
            const newElements = elements.filter((id, index) => index !== action.index);
            return setObjectValue(state, action.objectId, action.fieldPath, newElements.length > 0 ? newElements : undefined);
        }

        case MOVE_FIELD_ELEMENT: {

            const object = selectObjectById(state, action.objectId);
            let elements = _.get(object, action.fieldPath, []).slice(); // use slice to make sure a deep copy is created
            elements.splice(action.toIndex, 0, elements.splice(action.fromIndex, 1)[0]);
            return setObjectValue(state, action.objectId, action.fieldPath, elements);
        }

        case LOAD_OBJECT: {

            if (action.params.clearStack === true) {
                return clearStack(
                    state,
                    action.object.id || generateTemporaryObjectId(action.object.type),
                    action.object,
                    action.objectInfo,
                    {readOnly: action.params.readOnly}
                );
            }
            else {
                return pushObject(
                    state,
                    action.object.id || generateTemporaryObjectId(action.object.type),
                    action.object,
                    action.objectInfo,
                    {readOnly: action.params.readOnly}
                );
            }
        }

        case CREATE_NEW_OBJECT: {

            const objectType = action.newObject.type;
            const objectId = generateTemporaryObjectId(objectType);

            return clearStack(state, objectId, action.newObject, initialObjectInfo);
        }

        case CREATE_NEW_REFERENCE: {

            const objectType = action.newObject.type;
            const objectId = generateTemporaryObjectId(objectType);

            return pushObject(state, objectId, action.newObject, initialObjectInfo, {referenceFieldPath: action.fieldPath, flags: action.flags});
        }

        case CREATE_NEW_ELEMENT: {

            const objectType = action.newObject.type;
            const objectId = generateTemporaryObjectId(objectType);

            return pushObject(state, objectId, action.newObject, initialObjectInfo, {elementFieldPath: action.fieldPath});
        }

        case PUSH_ELEMENT: {

            return pushObject(state, generateTemporaryObjectId("element"), action.element, {
                ...initialObjectInfo,
                parentId: state.editStack.slice(-1)[0].objectId
            }, {
                elementFieldPath: action.fieldPath,
                readOnly: action.params.readOnly,
            });
        }

        case DELETE_OBJECT_TAG: {

            const object = selectObjectById(state, action.objectId);
            const newTags = object.tags.filter((value, index) => {return index !== action.index});

            return setObject(state, action.objectId, {
                ...object,
                tags: newTags
            }, false);
        }

        case ADD_OBJECT_TAG: {

            const object = selectObjectById(state, action.objectId);
            const newTags = object.tags.indexOf(action.tag) === -1 ? [...object.tags, action.tag] : object.tags;

            return setObject(state, action.objectId, {
                ...object,
                tags: newTags
            }, false);
        }

        case LOAD_ALL_TAGS: {

            return {
                ...state,
                allTags: action.tags
            }
        }

        case UPDATE_OBJECT_MAP: {

            return updateObjects(state, action.objectList);
        }

        case FETCH_REFERENCED_OBJECTS: {

            return updateObjects(state, action.objectList);
        }

        case FETCH_REFERENCABLE_OBJECTS: {

            return {
                ...updateObjects(state, action.objectList),
                referencableObjects: action.objectList
            };
        }

        default:
            return state;
    }
}